import {
    TemplateName,
    APIAppTemplateModel,
    getParameterSchema
} from "./AppTemplateModel"
import { MetricRequest, isSame } from "../Client/MetricRequest"
import { APIError, isAPIError } from "./Error"
import { LabelerConfig } from "../DataProgram/LabelerRestriction"
import { APIFeedbackTag } from "./JobFeedbackModel"
import { decodeSearchAt } from "../../Helpers/Functions/urlParse"
import { CollaboratorRole } from "./CollaboratorModel"
import { AppWorkerRole } from "./AppWorkerRole"
import { SuperTaskModel } from "./SuperTaskModel"
import { validate } from "../../Helpers/Schema/Schema"
import type { JSONSchema7 } from "json-schema"
import type { UiSchema } from "@rjsf/core"
import { AppSettings, AppSettingsUpdate } from "./AppSettings"

export enum AppInstanceSortBy {
    name = "name",
    uuid = "uuid",
    created = "created",
    status = "status",
    templateName = "templateName"
}

export enum AppInstanceOrderBy {
    asc = "asc",
    desc = "desc"
}

export enum AppInstanceStatus {
    ENABLED = "ENABLED",
    DISABLED = "DISABLED"
}

export type AppInstanceListParams = AppInstanceListSortParams &
    AppInstanceListStatusParams

export interface AppInstanceListStatusParams {
    showArchived?: boolean
}

export interface AppInstanceListSortParams {
    orderBy: AppInstanceOrderBy
    sortBy: AppInstanceSortBy
}

export interface JobStats {
    latest_completed_job_id: number
    calibration_to_be_reviewed: number
    /**
     * `null` if super.AI in-house crowd is enabled
     */
    collaborator_jobs: number | null
    /**
     * `null` if super.AI in-house crowd is enabled
     */
    completed_collaborator_jobs: number | null
    jobsUpdatedTime: string
}

export interface JobWithFeedbackStats {
    total: number
    totalUnread: number
    feedbackTagStats: FeedbackTagStats[]
}

export interface FeedbackTagStats extends APIFeedbackTag {
    jobCount?: number
}

export interface APIAppInstanceModel {
    appMetrics: {
        metric: null | {} | MetricRequest
    }
    appMetricsHistory: any
    appParams: null | { params: any }
    appParamsHistory: any
    canoticCrowd: boolean
    /**
     * Task routing constraints
     */
    constraints?: LabelerConfig
    created: string
    /**
     * List of workflows available in this app. Can be updated by sysadmin user.
     */
    dpWorkflows?: string[]
    enterprise: boolean
    inputSchema?: JSONSchema7
    inputUiSchema?: UiSchema
    /**
     * Note:
     * For a performance reason, *instance list* endpoint always returns null while *instance get* endpoint returns `JobStats`.
     */
    job_stats: JobStats | null | APIError
    metadata: any
    name: string
    outputSchema?: JSONSchema7
    outputUiSchema?: UiSchema
    ownerEmail: string
    ownerId: number
    ownerType: "ORGANIZATION" | "USER"
    ownerUsername: string
    projectName: string
    role: CollaboratorRole | null
    /**
     * Currently selected workflow for this app. Can be updated by sysadmin user and owner role user
     */
    selectedWorkflow?: string
    settings: AppSettings
    status: AppInstanceStatus
    superTasks?: SuperTaskModel[] | null
    templateName?: TemplateName | null
    uuid: string
    visibility?: string
    workerRole: AppWorkerRole | null
}

export interface APIAppInstanceMetaModel {
    totalArchived: number
}

export type JobsCompletedPerDay = [
    number,
    number,
    number,
    number,
    number,
    number,
    number
]

export interface APIAppInstancePatch {
    name?: string
    templateName?: string
    projectName?: string
    metadata?: any
    appParams?: null | { params: any }
    appMetrics?: {
        metric: MetricRequest
    }
    canoticCrowd?: boolean
    status?: AppInstanceStatus
    dpWorkflows?: string[]
    selectedWorkflow?: string
    constraints?: LabelerConfig
    cancelAndRelabelJobsInState?: string[]
    settings?: AppSettingsUpdate
}

export function decodeOrderBy(
    s: string | undefined
): AppInstanceOrderBy | undefined {
    if (s === AppInstanceOrderBy.asc) return AppInstanceOrderBy.asc
    if (s === AppInstanceOrderBy.desc) return AppInstanceOrderBy.desc
}

export function decodeSortBy(
    s: string | undefined
): AppInstanceSortBy | undefined {
    if (s === AppInstanceSortBy.name) return AppInstanceSortBy.name
    if (s === AppInstanceSortBy.uuid) return AppInstanceSortBy.uuid
    if (s === AppInstanceSortBy.created) return AppInstanceSortBy.created
    if (s === AppInstanceSortBy.status) return AppInstanceSortBy.status
    if (s === AppInstanceSortBy.templateName)
        return AppInstanceSortBy.templateName
}

export function decodeShowArchived(s: string | undefined): boolean | undefined {
    if (s === "true") return true
    return false
}

export const defaultParams: AppInstanceListParams = {
    orderBy: AppInstanceOrderBy.desc,
    sortBy: AppInstanceSortBy.created
}

export function isNonEmptyMetric(
    value: null | {} | MetricRequest
): value is MetricRequest {
    return value !== null && "quality" in value
}

export function isMetricsConfigured(
    instance: APIAppInstanceModel,
    template: APIAppTemplateModel
): boolean {
    return (
        isNonEmptyMetric(instance.appMetrics.metric) &&
        (!isNonEmptyMetric(template.defaultAppMetrics.metric) ||
            !isSame(
                template.defaultAppMetrics.metric,
                instance.appMetrics.metric
            ))
    )
}

export const getAppInstanceID = (model: APIAppInstanceModel) => model.uuid

export const isAppInstanceEnabled = (app?: APIAppInstanceModel) =>
    app?.status === AppInstanceStatus.ENABLED

export const isAppInstanceArchived = (app?: APIAppInstanceModel) =>
    app?.status === AppInstanceStatus.DISABLED

export function getLastCompletedJobID(
    instance: APIAppInstanceModel
): string | undefined {
    const stats = instance.job_stats

    if (stats === null || isAPIError(stats)) {
        return undefined
    }

    return "latest_completed_job_id" in stats &&
        stats.latest_completed_job_id !== 0
        ? String(stats.latest_completed_job_id)
        : undefined
}

export const filterFeedbackTags = (feedbackTags?: FeedbackTagStats[]) =>
    feedbackTags?.filter(
        tag =>
            tag.position > -3 && tag.jobCount !== undefined && tag.jobCount > 0
    )

// encode for browser search params
export function encodeParams(
    params: AppInstanceListParams
): URLSearchParams | undefined {
    const search = new URLSearchParams()

    if (params.orderBy !== defaultParams.orderBy)
        search.append("orderBy", params.orderBy)
    if (params.sortBy !== defaultParams.sortBy)
        search.append("sortBy", params.sortBy)
    if (params.showArchived) search.append("showArchived", "true")

    return Array.from(search).length > 0 ? search : undefined
}

// encode for api request
export function encodeAPIAppInstanceListParams(
    params: AppInstanceListParams
): URLSearchParams {
    return encodeAppInstanceListSortParams(
        params,
        encodeAppInstanceListStatusParams(params)
    )
}

function encodeAppInstanceListStatusParams({
    showArchived
}: AppInstanceListStatusParams): URLSearchParams {
    const search = new URLSearchParams()

    let statuses = [AppInstanceStatus.ENABLED]
    if (showArchived) {
        statuses = [AppInstanceStatus.ENABLED, AppInstanceStatus.DISABLED]
    }
    statuses.forEach(s => search.append("statusIn", s))
    return search
}

function encodeAppInstanceListSortParams(
    { sortBy, orderBy }: AppInstanceListSortParams,
    baseSearch?: URLSearchParams
) {
    const search = new URLSearchParams(baseSearch)

    search.set("orderBy", orderBy)
    search.set("sortBy", sortBy)

    return search
}

export function decodeParams(search: URLSearchParams): AppInstanceListParams {
    return {
        orderBy:
            decodeSearchAt(search, "orderBy", decodeOrderBy) ||
            defaultParams.orderBy,
        sortBy:
            decodeSearchAt(search, "sortBy", decodeSortBy) ||
            defaultParams.sortBy,
        showArchived: decodeSearchAt(search, "showArchived", decodeShowArchived)
    }
}

export function hasValidAppParams(
    instance: APIAppInstanceModel,
    template: APIAppTemplateModel
): boolean {
    const schema = getParameterSchema(template)

    return (
        schema === undefined ||
        validate(schema.schema, instance.appParams?.params ?? null).valid
    )
}

export const mapAppInstanceStatus = (status: AppInstanceStatus) => {
    switch (status) {
        case AppInstanceStatus.ENABLED:
            return "ENABLED"
        case AppInstanceStatus.DISABLED:
            return "ARCHIVED"
    }
}
